import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import {
    AlertTypes,
    GenericFormContainer,
    Grid,
    Leap4Breadcrumbs,
    Mode,
    Mui,
    MuiIcons,
    setAlert,
    Spinner,
} from '@leap4/frontend';
import { urlService } from '../../routing/urlsService';
import {
    ITrackedPublisher,
    initialTrackedPublisher,
    resolveAppAdsTxtUrl,
    RESPONSIVENESS_LABELS,
} from './appAdsLines.interface';
import {
    createPublisher,
    archivePublisher,
    errorMessage,
    fetchLines,
    fetchPublisher,
    setPublisherActive,
    updatePublisher,
} from './appAdsLines.api';
import { ArchivePublisherDialog } from './archivePublisherDialog';
import { BackButton } from './backButton';

const { Box, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography } = Mui;
const { Create: CreateIcon } = MuiIcons;

const connector = connect(() => ({}), { setAlert });
type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * Create/edit a tracked publisher. Structurally the same as manageDemand.tsx: a
 * GenericFormContainer in Create or Edit mode with the shared breadcrumbs.
 */
const _manageTrackedPublisher: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const { id } = useParams<{ id?: string }>();
    const queryClient = useQueryClient();
    const history = useHistory();
    const isEdit = !!id;

    const [publisher, setPublisher] = useState<ITrackedPublisher>(initialTrackedPublisher);
    // Held as text rather than as the array, so typing a comma doesn't split mid-word and
    // the cursor stays put. Split once on save.
    const [managersText, setManagersText] = useState('');
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [togglingActive, setTogglingActive] = useState(false);

    const { data: loaded, isLoading, isError } = useQuery(
        ['trackedPublisher', id],
        () => fetchPublisher(id),
        { enabled: isEdit, refetchOnWindowFocus: false },
    );

    useEffect(() => {
        if (!loaded) return;
        setPublisher({ ...initialTrackedPublisher, ...loaded });
        setManagersText((loaded.accountManagers || []).join(', '));
    }, [loaded]);


    // Counts explain what will be preserved in the archive - the form itself has no use
    // for the lines.
    const { data: lines } = useQuery(
        ['trackedLines', loaded?._id],
        () => fetchLines(loaded._id),
        { enabled: !!loaded?._id, refetchOnWindowFocus: false },
    );

    const domainValue = (publisher.domain || '').trim();
    /**
     * Required, and that's the whole rule. There is no format check on either the domain or
     * the app-ads.txt override: publishers host their files in genuinely odd places, and
     * refusing an unusual one meant ad-ops couldn't track that publisher at all. A wrong value
     * fails its next check and says so in the Read status column.
     */
    const domainError = submitted && !domainValue ? 'Domain is required' : '';

    const errors = {
        name: submitted && !publisher.name?.trim() ? 'Name is required' : '',
        domain: domainError,
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPublisher((prev) => ({ ...prev, [name]: value }));
    };

    const isParked = loaded?.isActive === false;

    const onToggleActive = async () => {
        try {
            setTogglingActive(true);
            await setPublisherActive(loaded._id, isParked);
            setAlert({
                msg: isParked
                    ? 'Publisher is active again.'
                    : 'Publisher parked. Its lines are kept, but it is skipped by checks and bulk actions.',
                type: AlertTypes.SUCCESS,
            });
            // The button's label comes from the loaded record, so it has to be refetched rather
            // than assumed - and the grids behind this screen change too.
            await queryClient.invalidateQueries(['trackedPublisher', id]);
            queryClient.invalidateQueries('trackedPublishers');
            queryClient.invalidateQueries('distinctLineSpecs');
            queryClient.invalidateQueries('oldLines');
        } catch (err: any) {
            setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE });
        } finally {
            setTogglingActive(false);
        }
    };

    const onSubmit = async () => {
        setSubmitted(true);
        if (!publisher.name?.trim() || !domainValue) return;

        try {
            setSaving(true);
            const payload: Partial<ITrackedPublisher> = {
                name: publisher.name.trim(),
                // The server splits a pasted URL into host + override; sending the raw value
                // keeps that one implementation rather than second-guessing it here.
                domain: publisher.domain.trim(),
                // Stored with its scheme, so the value that comes back is unambiguous
                // whether it's opened in a tab or handed to the fetcher.
                appAdsTxtUrl: publisher.appAdsTxtUrl?.trim()
                    ? resolveAppAdsTxtUrl({ appAdsTxtUrl: publisher.appAdsTxtUrl })
                    : undefined,
                responsiveness: Number(publisher.responsiveness) || 3,
                contactEmail: publisher.contactEmail?.trim() || undefined,
                notes: publisher.notes?.trim() || undefined,
                sellerId: publisher.sellerId?.trim() || undefined,
                // Whitespace and blanks are tidied server-side too; the payload is a
                // whitelist, so a field missing here is simply never saved.
                accountManagers: managersText
                    .split(',')
                    .map((n) => n.trim())
                    .filter(Boolean),
            };

            if (isEdit) await updatePublisher(loaded._id, payload);
            else await createPublisher(payload);

            setAlert({ msg: 'Publisher saved', type: AlertTypes.SUCCESS });
            history.push(urlService.appAdsLines());
        } catch (err: any) {
            // errorMessage unwraps the server's text; err.message is just "Request failed with
            // status code 409" and loses the duplicate-domain explanation entirely.
            setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE });
        } finally {
            setSaving(false);
        }
    };

    if (isError) return <>Could not load this publisher.</>;
    if (isEdit && isLoading) return <Spinner />;
    if (saving) return <Spinner />;

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Leap4Breadcrumbs
                    breadcrumbs={[
                        { text: 'Publisher Lines', link: urlService.appAdsLines(), icon: <CreateIcon /> },
                        { text: isEdit ? 'Edit publisher' : 'Add publisher', icon: <CreateIcon /> },
                    ]}
                />
                {/* Editing came from that publisher's own page, so that's its parent; adding
                    has no publisher yet and belongs to the list. */}
                <BackButton
                    to={isEdit ? urlService.trackedPublisher(id) : urlService.appAdsLines()}
                />
            </Box>

            <GenericFormContainer
                mode={isEdit ? Mode.Edit : Mode.Create}
                onCreateOrEditFunc={onSubmit}
                deleteText="Archive publisher"
                deleteIcon={<MuiIcons.Delete />}
                disabled={archiving}
                // The form action only opens the confirmation.
                onDeleteFunction={isEdit ? () => setConfirmArchive(true) : undefined}
            >
                <Typography variant="h5" fontWeight={600} style={{ paddingBottom: 20 }}>
                    {isEdit ? 'Edit publisher' : 'Add publisher'}
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            name="name"
                            label="Name"
                            value={publisher.name || ''}
                            onChange={onChange}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            name="domain"
                            label="Domain"
                            placeholder="example.com"
                            value={publisher.domain || ''}
                            onChange={onChange}
                            error={!!errors.domain}
                            helperText={
                                errors.domain ||
                                'Just the host, e.g. acme.com. Paste a full URL and the address moves to the override below.'
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="appAdsTxtUrl"
                            label="app-ads.txt URL override"
                            value={publisher.appAdsTxtUrl || ''}
                            onChange={onChange}
                            helperText="Only needed when the file isn't at the default location. Any URL — some publishers host theirs somewhere unusual."
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="sellerId"
                            label="sellers.json seller ID"
                            value={publisher.sellerId || ''}
                            onChange={onChange}
                            helperText="Optional. Needed only to generate our own DIRECT/RESELLER line for this publisher"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="responsiveness-label">Responsiveness</InputLabel>
                            <Select
                                labelId="responsiveness-label"
                                label="Responsiveness"
                                value={publisher.responsiveness ?? 3}
                                onChange={(e) =>
                                    setPublisher((prev) => ({ ...prev, responsiveness: Number(e.target.value) }))
                                }
                            >
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {RESPONSIVENESS_LABELS[n]}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>How reliably they apply requested changes. Informational only.</FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="contactEmail"
                            label="Ad-ops contact email"
                            value={publisher.contactEmail || ''}
                            onChange={onChange}
                            helperText="Where the updated publisher list gets sent"
                        />
                    </Grid>

                    {/* Free text on purpose: the staff list lives outside PAPA2, so there is
                        nothing to validate against and nothing worth refusing. Several names
                        go in the one box, separated by commas. */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="accountManagers"
                            label="Account managers"
                            placeholder="Nadav Asher, Idan"
                            value={managersText}
                            onChange={(e) => setManagersText(e.target.value)}
                            helperText="Who to ask about this publisher. Separate several names with commas."
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            name="notes"
                            label="Notes"
                            value={publisher.notes || ''}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>

                {/* Parking lives here as well as on the grid: this is where you come when you
                    are done with a publisher, and it saves a trip back to the list. Only on
                    edit - there is nothing to park until the publisher exists. */}
                {isEdit && (
                    <Box mt={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            color={isParked ? 'success' : 'warning'}
                            disabled={togglingActive}
                            onClick={onToggleActive}
                        >
                            {isParked ? 'Activate publisher' : 'Deactivate publisher'}
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            {isParked
                                ? 'Parked: kept in full, but skipped by checks, bulk actions and the all-lines view.'
                                : 'Active. Deactivating keeps everything — it only stops this publisher being worked.'}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ height: 32 }} />
            </GenericFormContainer>

            <ArchivePublisherDialog
                publisher={confirmArchive ? loaded : null}
                activeLines={lines?.active?.length}
                removalRequests={lines?.removed?.length}
                busy={archiving}
                onCancel={() => setConfirmArchive(false)}
                onConfirm={async () => {
                    if (!loaded || archiving) return;
                    try {
                        setArchiving(true);
                        await archivePublisher(loaded._id);
                        setAlert({ msg: 'Publisher archived. Restore it from the Archive tab.', type: AlertTypes.SUCCESS });
                        queryClient.invalidateQueries('trackedPublishers');
                        queryClient.invalidateQueries('archivedPublishers');
                        queryClient.invalidateQueries('distinctLineSpecs');
                        queryClient.invalidateQueries('oldLines');
                        setConfirmArchive(false);
                        history.push(urlService.appAdsLines());
                    } catch (err: any) {
                        setAlert({ msg: `Could not archive: ${errorMessage(err)}`, type: AlertTypes.FAILURE });
                    } finally {
                        setArchiving(false);
                    }
                }}
            />
        </>
    );
};

export const ManageTrackedPublisher = connector(_manageTrackedPublisher);
export default ManageTrackedPublisher;
